import { useCallback, useMemo } from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import { Icon, Text, useTheme } from 'react-native-paper'
import { useBatchUsers } from '@/hooks/useBatchUsers'
import {
    formatDateShort,
    formatDurationHHMM,
    getNetWorkSeconds,
    sumNetWorkSeconds,
} from '@/lib/dates'
import { displayLocationName } from '@/lib/location-utils'
import { isTravelRouteNotes } from '@/lib/movement-utils'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { EmptyCard } from '@/ui/EmptyCard'
import { LoadingIndicator } from '@/ui/LoadingIndicator'

interface ApprovedTimeEntriesTableProps {
    entries: TimeEntriesExpanded[]
    isLoading: boolean
    userColorMap?: Map<string, string>
}

function calculateTotalSeconds(entries: TimeEntriesExpanded[]): number {
    return sumNetWorkSeconds(entries)
}

const getEntryTypeIcon = (entryType: string) => {
    switch (entryType) {
        case 'manual':
            return 'pencil'
        case 'timer':
            return 'timer-outline'
        case 'geofence':
            return 'map-marker-radius'
        case 'travel':
            return 'car'
        case 'break':
            return 'coffee-outline'
        default:
            return 'circle-outline'
    }
}

export function ApprovedTimeEntriesTable({
    entries,
    isLoading,
    userColorMap,
}: ApprovedTimeEntriesTableProps) {
    const theme = useTheme()
    const totalSeconds = calculateTotalSeconds(entries)

    const userIds = useMemo(() => {
        return Array.from(new Set(entries.map(e => e.user)))
    }, [entries])
    const { userMap } = useBatchUsers(userIds)

    const renderHeader = () => (
        <View style={[styles.headerRow, { borderBottomColor: theme.colors.outlineVariant }]}>
            <View style={styles.typeColumn} />
            <Text
                style={[
                    styles.headerText,
                    styles.userColumn,
                    { color: theme.colors.onSurfaceVariant },
                ]}
            >
                User
            </Text>
            <Text
                style={[
                    styles.headerText,
                    styles.locationColumn,
                    { color: theme.colors.onSurfaceVariant },
                ]}
            >
                Location
            </Text>
            <Text
                style={[
                    styles.headerText,
                    styles.dateColumn,
                    { color: theme.colors.onSurfaceVariant },
                ]}
            >
                Date
            </Text>
            <Text style={[styles.headerText, styles.hoursColumn, { color: theme.colors.primary }]}>
                {formatDurationHHMM(totalSeconds)}
            </Text>
        </View>
    )

    const renderItem = useCallback(
        ({ item: entry }: { item: TimeEntriesExpanded }) => {
            const userName = userMap.get(entry.user)?.name ?? entry.expand?.user?.name ?? 'Unknown'
            const isTravelWithRoute =
                entry.entry_type === 'travel' && isTravelRouteNotes(entry.notes)
            const locationName = isTravelWithRoute
                ? entry.notes?.split('\n')[0]
                : displayLocationName(entry.expand?.location?.name) || '—'
            const workSeconds = getNetWorkSeconds(entry)
            const userColor = userColorMap?.get(entry.user)

            return (
                <View style={[styles.row, { borderBottomColor: theme.colors.surfaceVariant }]}>
                    <View style={styles.typeColumn}>
                        <Icon
                            source={getEntryTypeIcon(entry.entry_type)}
                            size={14}
                            color={theme.colors.onSurfaceVariant}
                        />
                    </View>
                    <Text
                        style={[
                            styles.cellText,
                            styles.userColumn,
                            { color: userColor || theme.colors.onSurface },
                        ]}
                        numberOfLines={1}
                    >
                        {userName}
                    </Text>
                    <Text
                        style={[
                            styles.cellText,
                            styles.locationColumn,
                            { color: theme.colors.onSurfaceVariant },
                        ]}
                        numberOfLines={1}
                    >
                        {locationName}
                    </Text>
                    <Text
                        style={[
                            styles.cellText,
                            styles.dateColumn,
                            { color: theme.colors.onSurface },
                        ]}
                        numberOfLines={1}
                    >
                        {formatDateShort(entry.clock_in)}
                    </Text>
                    <Text
                        style={[
                            styles.hoursText,
                            styles.hoursColumn,
                            { color: theme.colors.primary },
                        ]}
                        numberOfLines={1}
                    >
                        {formatDurationHHMM(workSeconds)}
                    </Text>
                </View>
            )
        },
        [theme, userMap, userColorMap]
    )

    const renderEmpty = () => (
        <EmptyCard
            icon="timetable"
            title="No approved time entries"
            subtitle="Approved time entries for this job will appear here"
        />
    )

    if (isLoading) {
        return <LoadingIndicator />
    }

    return (
        <FlatList
            data={entries}
            keyExtractor={item => item.id}
            renderItem={renderItem}
            ListHeaderComponent={entries.length > 0 ? renderHeader : null}
            ListEmptyComponent={renderEmpty}
            style={styles.list}
            scrollEnabled={false}
        />
    )
}

const styles = StyleSheet.create({
    list: {
        //marginHorizontal: -32,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderBottomWidth: 1,
    },
    headerText: {
        fontSize: 11,
        fontWeight: '600',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
    },
    typeColumn: {
        width: 28,
        justifyContent: 'center',
        alignItems: 'center',
    },
    userColumn: {
        flex: 1,
        paddingRight: 4,
    },
    locationColumn: {
        flex: 1,
        paddingRight: 4,
    },
    dateColumn: {
        width: 50,
        paddingRight: 4,
    },
    hoursColumn: {
        width: 46,
        textAlign: 'right',
    },
    cellText: {
        fontSize: 12,
    },
    hoursText: {
        fontSize: 12,
        fontWeight: '600',
    },
})
